import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CustomChevronCircleIcon } from "../../../assets/icon";
import {
  PreassessmentQuestion,
  AssessmentQuestion,
  AssessmentAnswerResponse,
  AssessmentLeaderboardItem,
} from "../../../axios/quiz";
import type { AssessmentInteractiveSubmitGetterRef } from "../../interactive-questions/assessment-mode";
import { AssessmentHeaderQuiz } from "../quiz-content/assessment-header-quiz/assessment-header-quiz";
import {
  mobileQuestionLabelStyle,
  mobileSubheaderRowStyle,
} from "../quiz-content/assessment-header-quiz/styles";
import { QuizMainBlock } from "../quiz-content/quiz-main-block/quiz-main-block";
import { AssessmentLeaderboardCard } from "../assessment-leaderboard-card/assessment-leaderboard-card";
import { AssessmentContestTopBar } from "../assessment-contest-top-bar/assessment-contest-top-bar";
import { AssessmentQuestionWinnerCard } from "../assessment-question-winner-card/assessment-question-winner-card";
import { AssessmentStreakBadge } from "../assessment-streak-badge/assessment-streak-badge";
import type { AssessmentRunningScoreBreakdown } from "../quiz-results-circular/quiz-results-circular";

import { assessmentPhoneLandscapeQuery, assessmentShortViewportQuery } from "../assessment-layout-styles";
import {
  containerStyle,
  headerBoxStyle,
  contentBoxStyle,
  footerBoxStyle,
  footerActionsStyle,
  footerPrimaryActionsStyle,
  buttonStyle,
  iconBoxStyle,
  previousIconBoxStyle,
  retryIconBoxStyle,
  mobileBottomLeaderboardStyle,
  mobileTopPanelsStyle,
} from "./styles";

interface AssessmentQuizContentProps {
  currentQuestion: PreassessmentQuestion | AssessmentQuestion;
  currentQuestionIndex: number;
  totalQuestions: number;
  selectedAnswer: string;
  isAnswerChecked: boolean;
  isAnswerSelected: boolean;
  isSubmitting: boolean;
  onAnswerSelect: (answer: string) => void;
  onCheckAnswer: () => void;
  onRetryAnswer?: () => void;
  onConfirmAnswer?: () => void;
  answerResetKey?: number;
  openTextFocusRequestKey?: number;
  openTextValidationError?: string | null;
  openTextLocked?: boolean;
  onPreviousQuestion?: () => void;
  onNextQuestion: () => void;
  onSubmitAndNextQuestion?: () => void;
  onFinish: () => void;
  onClose: () => void;
  remainingSeconds: number;
  totalSeconds: number;
  totalMaxScore: number;
  scoresEarned: number;
  bonusScore?: number;
  scoreBreakdown?: AssessmentRunningScoreBreakdown | null;
  answeredQuestions?: number;
  answerResponse?: AssessmentAnswerResponse | null;
  leaderboard?: AssessmentLeaderboardItem[];
  currentUserPlace?: number | null;
  gamificationEnabled?: boolean;
  showFeedbackMode?: "not_show" | "after_answer" | "after_completion";
  answerEditingEnabled?: boolean;
  isAnswerCommitted?: boolean;
  editSubmitKind?: "next" | "submit" | null;
  canNavigatePrevious?: boolean;
  assessmentId?: string | null;
  contestTitle?: string | null;
  managerActions?: React.ReactNode;
  interactiveSubmitGetterRef?: AssessmentInteractiveSubmitGetterRef;
  onInteractiveCompletionChange?: (isComplete: boolean) => void;
  isInteractiveAnswerChecked?: boolean;
  isCheckAnswerBlocked?: boolean;
  interactiveCheckedState?: {
    userAnswers?: Record<string, string>;
    userOrder?: string[];
    userPairs?: Array<{ left_id: string; right_id: string }>;
    isCorrect?: boolean;
    feedback?: string;
  };
}

export const AssessmentQuizContent: React.FC<AssessmentQuizContentProps> = ({
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  selectedAnswer,
  isAnswerChecked,
  isAnswerSelected,
  isSubmitting,
  onAnswerSelect,
  onCheckAnswer,
  onRetryAnswer,
  onConfirmAnswer,
  answerResetKey = 0,
  openTextFocusRequestKey = 0,
  openTextValidationError = null,
  openTextLocked = false,
  onPreviousQuestion,
  onNextQuestion,
  onSubmitAndNextQuestion,
  onFinish,
  onClose,
  remainingSeconds,
  totalSeconds,
  totalMaxScore,
  scoresEarned,
  bonusScore,
  scoreBreakdown = null,
  answeredQuestions,
  answerResponse,
  leaderboard,
  currentUserPlace,
  gamificationEnabled = true,
  showFeedbackMode = "after_answer",
  answerEditingEnabled = false,
  isAnswerCommitted = false,
  editSubmitKind = null,
  canNavigatePrevious,
  assessmentId = null,
  contestTitle = null,
  managerActions,
  interactiveSubmitGetterRef,
  onInteractiveCompletionChange,
  isInteractiveAnswerChecked,
  isCheckAnswerBlocked,
  interactiveCheckedState,
}) => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const isPhoneLandscape = useMediaQuery(assessmentPhoneLandscapeQuery);
  const isShortViewport = useMediaQuery(assessmentShortViewportQuery);
  const isCompactLayout = isMobileLayout || isPhoneLandscape;
  const bottomLeaderboardMaxItems =
    isMobileLayout || isPhoneLandscape ? 1 : 3;
  const [isLeaderboardExpanded, setIsLeaderboardExpanded] = useState(false);
  const showMobileStreak =
    gamificationEnabled &&
    isAnswerChecked &&
    typeof answerResponse?.streak === "number" &&
    answerResponse.streak > 0;
  const isEvaluatingAnswer = isSubmitting;
  // Show per-answer feedback whenever after_answer is configured (including edit mode).
  const revealAnswerFeedback = showFeedbackMode === "after_answer";
  const isCheckAnswerDisabled =
    !isAnswerSelected ||
    isEvaluatingAnswer ||
    isAnswerChecked ||
    Boolean(isCheckAnswerBlocked);
  const isNextDisabledInEditMode =
    !isAnswerSelected || isSubmitting || Boolean(isCheckAnswerBlocked);
  const isLastQuestion = currentQuestionIndex >= totalQuestions - 1;
  const isFinishDisabled = isNextDisabledInEditMode;
  // After Submit & Next the answer is locked — keep the button visible but disabled
  // until Retry unlocks it again. Soft Next does not disable Submit.
  const isSubmitAndNextDisabled =
    isNextDisabledInEditMode || isAnswerCommitted;
  const showLiveLeaderboard = gamificationEnabled;
  const showPreviousButton =
    answerEditingEnabled &&
    Boolean(onPreviousQuestion) &&
    (canNavigatePrevious ?? currentQuestionIndex > 0);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevQuestionIndexRef = useRef<number>(currentQuestionIndex);
  const isBonusQuestion =
    (currentQuestion as AssessmentQuestion).is_bonus === true ||
    (currentQuestion as AssessmentQuestion).question_category === "bonus";
  const bonusQuestionChip = isBonusQuestion ? (
    <Chip size="small" label="Bonus question" color="warning" />
  ) : null;
  const resolvedContestTitle =
    typeof contestTitle === "string" && contestTitle.trim()
      ? contestTitle.trim()
      : null;

  useEffect(() => {
    if (
      containerRef.current &&
      prevQuestionIndexRef.current !== currentQuestionIndex
    ) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      prevQuestionIndexRef.current = currentQuestionIndex;
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    setIsLeaderboardExpanded(false);
  }, [currentQuestionIndex]);

  return (
    <Box sx={containerStyle}>
      <Box sx={headerBoxStyle}>
        <AssessmentContestTopBar
          contestTitle={resolvedContestTitle}
          currentUserPlace={currentUserPlace}
          leaderboard={leaderboard}
          actions={managerActions}
          gamificationEnabled={gamificationEnabled}
        />
        <AssessmentHeaderQuiz
          totalAnswers={totalQuestions}
          remainingSeconds={remainingSeconds}
          totalSeconds={totalSeconds}
          totalMaxScore={totalMaxScore}
          scoresEarned={scoresEarned}
          bonusScore={bonusScore}
          scoreBreakdown={scoreBreakdown}
          answeredQuestions={answeredQuestions}
          hidePointsEarned={
            answerEditingEnabled && showFeedbackMode !== "after_answer"
          }
          mobileSubheader={
            isCompactLayout ? (
              <Box sx={mobileSubheaderRowStyle}>
                <Typography sx={mobileQuestionLabelStyle}>
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </Typography>
                {bonusQuestionChip}
                {showMobileStreak ? (
                  <AssessmentStreakBadge streakCount={answerResponse?.streak ?? 0} />
                ) : null}
              </Box>
            ) : undefined
          }
        />
        {showLiveLeaderboard &&
        isCompactLayout &&
        (!isShortViewport || isAnswerChecked) ? (
          <Box sx={mobileTopPanelsStyle}>
            <AssessmentQuestionWinnerCard
              questionWinner={
                isAnswerChecked ? answerResponse?.question_winner : null
              }
            />
          </Box>
        ) : null}
      </Box>
      <Box sx={contentBoxStyle} ref={containerRef} data-assessment-scroll>
        <QuizMainBlock
          key={`${currentQuestionIndex}-${answerResetKey}`}
          question={currentQuestion}
          questionIndex={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isAnswerChecked={isAnswerChecked}
          answerResponse={answerResponse}
          isSubmitting={isSubmitting}
          hideQuestionHeader={isCompactLayout}
          hideQuestionWinner={isCompactLayout || !gamificationEnabled}
          hideStreak={isCompactLayout || !gamificationEnabled}
          interactiveSubmitGetterRef={interactiveSubmitGetterRef}
          onInteractiveCompletionChange={onInteractiveCompletionChange}
          isInteractiveAnswerChecked={isInteractiveAnswerChecked}
          interactiveCheckedState={interactiveCheckedState}
          revealAnswerFeedback={revealAnswerFeedback}
          allowAnswerEditing={answerEditingEnabled}
          openTextFocusRequestKey={openTextFocusRequestKey}
          openTextValidationError={openTextValidationError}
          openTextLocked={openTextLocked}
          isAnswerFinalized={answerEditingEnabled && isAnswerCommitted}
          assessmentId={assessmentId}
          assessmentQuestionIndex={currentQuestionIndex}
          questionBadge={isCompactLayout ? null : bonusQuestionChip}
          sidebar={
            showLiveLeaderboard && !isCompactLayout ? (
              <AssessmentLeaderboardCard
                items={leaderboard}
                currentUserPlace={currentUserPlace}
                title="Leaderboard"
                maxItems={3}
                showExpandAction
              />
            ) : undefined
          }
        />
        {showLiveLeaderboard && isCompactLayout && isShortViewport ? (
          <Box
            sx={mobileBottomLeaderboardStyle(
              isPhoneLandscape,
              isLeaderboardExpanded,
              isShortViewport,
            )}
          >
            <AssessmentLeaderboardCard
              key={currentQuestionIndex}
              items={leaderboard}
              currentUserPlace={currentUserPlace}
              title="Leaderboard"
              maxItems={bottomLeaderboardMaxItems}
              compact
              showExpandAction
              onExpandedChange={setIsLeaderboardExpanded}
            />
          </Box>
        ) : null}
      </Box>
      {showLiveLeaderboard && isCompactLayout && !isShortViewport ? (
        <Box
          sx={mobileBottomLeaderboardStyle(
            isPhoneLandscape,
            isLeaderboardExpanded,
            isShortViewport,
          )}
        >
          <AssessmentLeaderboardCard
            key={currentQuestionIndex}
            items={leaderboard}
            currentUserPlace={currentUserPlace}
            title="Leaderboard"
            maxItems={bottomLeaderboardMaxItems}
            compact
            showExpandAction
            onExpandedChange={setIsLeaderboardExpanded}
          />
        </Box>
      ) : null}
      <Box sx={footerBoxStyle(theme, isEvaluatingAnswer)}>
        <Box sx={footerActionsStyle}>
          {showPreviousButton ? (
            <Button
              onClick={onPreviousQuestion}
              disabled={isSubmitting}
              sx={buttonStyle(theme, isSubmitting)}
            >
              <Box sx={previousIconBoxStyle}>
                <CustomChevronCircleIcon />
              </Box>
              Previous
            </Button>
          ) : null}
          <Box sx={footerPrimaryActionsStyle}>
            {answerEditingEnabled ? (
              <>
                <Button
                  onClick={onRetryAnswer}
                  disabled={
                    isSubmitting || !isAnswerSelected || isAnswerCommitted
                  }
                  sx={buttonStyle(
                    theme,
                    isSubmitting || !isAnswerSelected || isAnswerCommitted,
                  )}
                >
                  Retry
                  <Box sx={retryIconBoxStyle}>
                    <CustomChevronCircleIcon />
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  onClick={isLastQuestion ? onFinish : onNextQuestion}
                  disabled={
                    isLastQuestion ? isFinishDisabled : isNextDisabledInEditMode
                  }
                  sx={buttonStyle(
                    theme,
                    isLastQuestion ? isFinishDisabled : isNextDisabledInEditMode,
                  )}
                >
                  {editSubmitKind === "next"
                    ? "Saving..."
                    : isLastQuestion
                      ? "Finish"
                      : "Next Question"}
                  <Box sx={iconBoxStyle}>
                    <CustomChevronCircleIcon />
                  </Box>
                </Button>
                {onSubmitAndNextQuestion && !isLastQuestion ? (
                  <Button
                    variant="contained"
                    onClick={onSubmitAndNextQuestion}
                    disabled={isSubmitAndNextDisabled}
                    sx={buttonStyle(theme, isSubmitAndNextDisabled)}
                  >
                    {editSubmitKind === "submit"
                      ? "Submitting..."
                      : "Submit & Next Question"}
                    <Box sx={iconBoxStyle}>
                      <CustomChevronCircleIcon />
                    </Box>
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                {!isAnswerChecked ? (
                  <Button
                    onClick={onCheckAnswer}
                    disabled={isCheckAnswerDisabled}
                    sx={buttonStyle(theme, isCheckAnswerDisabled)}
                  >
                    Check Answer
                    <Box sx={iconBoxStyle}>
                      <CustomChevronCircleIcon />
                    </Box>
                  </Button>
                ) : null}
                {isAnswerCommitted ? (
                  <Button
                    variant="contained"
                    onClick={
                      currentQuestionIndex < totalQuestions - 1
                        ? onNextQuestion
                        : onFinish
                    }
                    disabled={isSubmitting}
                    sx={buttonStyle(theme, isSubmitting)}
                  >
                    {currentQuestionIndex < totalQuestions - 1
                      ? "Next question"
                      : "Finish"}
                    <Box sx={iconBoxStyle}>
                      <CustomChevronCircleIcon />
                    </Box>
                  </Button>
                ) : null}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentQuizContent;
